import React, { useState, useEffect, useContext } from "react";
import { adminGet } from '../adminFetch';
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../AuthContext';
import "./AdminAddCategory.css";
import Swal from 'sweetalert2';
import { LoadingContext } from "../LoadingContext";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const fetchCategories = async () => {
  const response = await adminGet(`${API_BASE_URL}/api/categories`);
  return response.json();
};

export const addCategory = async (categoryData, authFetch) => {

  const formData = new FormData();
  formData.append("Name", categoryData.name);
  formData.append("Description", categoryData.description);
  
  if (categoryData.parentCategoryId) {
    formData.append("ParentCategoryId", categoryData.parentCategoryId);
  }
  
  if (categoryData.imageFile) {
    formData.append("ImageFile", categoryData.imageFile);
  }

  const response = await authFetch(`${API_BASE_URL}/api/categories`, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Failed to add category");
  }

  return response.json();
};

const AdminAddCategory = () => {
  const { authFetch } = useContext(AuthContext);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [parentCategoryId, setParentCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { setIsLoading, isLoading } = useContext(LoadingContext);

  useEffect(() => {
    const loadCategories = async () => {
      setIsLoading(true);
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (err) {
        setError("Failed to load categories");
        Swal.fire({
          icon: 'error',
          title: 'Loading Error',
          text: 'Failed to load categories',
          confirmButtonColor: '#45a049'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, [setIsLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!imageFile) {
      Swal.fire({
        icon: 'warning',
        title: 'Image Required',
        text: 'Please upload an image for the category.',
        confirmButtonColor: '#45a049'
      });
      return;
    }
    
    Swal.fire({
      title: 'Adding Category',
      text: 'Please wait while we add your category...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    setIsLoading(true);

    const categoryData = {
      name,
      description,
      imageFile,
      parentCategoryId: parentCategoryId === "" ? null : parseInt(parentCategoryId),
    };

    try {
      await addCategory(categoryData, authFetch);
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Category added successfully!',
        timer: 1500,
        confirmButtonColor: '#45a049'
      }).then(() => {
        navigate('/admin/category/');
      });
    } catch (error) {
      setError("Error adding category: " + error.message);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: "Error adding category: " + error.message,
        confirmButtonColor: '#e74c3c'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'Please select an image under 5MB.',
          confirmButtonColor: '#e74c3c'
        });
        return;
      }
      
      if (!file.type.match('image.*')) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File Type',
          text: 'Please select a valid image file (JPEG, PNG, etc).',
          confirmButtonColor: '#e74c3c'
        });
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    Swal.fire({
      title: 'Remove Image?',
      text: 'Are you sure you want to remove this image?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes'
    }).then((result) => {
      if (result.isConfirmed) {
        setImageFile(null);
        setImagePreview("");
      }
    });
  };


  if (isLoading) {
    return (
      <div className="admin-add-category-loading">
        <div style={{ textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '40px', color: '#45a049', marginBottom: '15px' }}></i>
          <p>Loading categories...</p>
        </div>
      </div>
    );
  }

  if (error && !imagePreview) {
    return (
      <div className="admin-add-category-container">
        <div className="admin-add-category-error">
          <i className="fas fa-exclamation-circle" style={{ marginRight: '10px' }}></i>
          {error}
        </div>
        <button 
          className="admin-add-category-back-btn" 
          onClick={() => navigate('/admin/category')}
        >
          <i className="fas fa-arrow-left"></i> Back to Categories
        </button>
      </div>
    );
  }

  return (
    <div className="admin-add-category-container">
      <div className="admin-add-category-header">
        <button 
          className="admin-add-category-back-btn" 
          onClick={() => navigate('/admin/category')}
        >
          <i className="fas fa-arrow-left"></i> Back to Categories
        </button>
      </div>
      
      <div className="admin-add-category-title">
        <h1>Add New Category</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-add-category-details-form">
        <div className="admin-add-category-form-group">
          <label>Category Name:</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter category name"
            required
          />
        </div>

        <div className="admin-add-category-form-group">
          <label>Description:</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter category description"
            required
          />
        </div>

        <div className="admin-add-category-form-group">
          <label>Parent Category (Optional):</label>
          <select
            value={parentCategoryId}
            onChange={(e) => setParentCategoryId(e.target.value)}
          >
            <option value="">None (Top-Level Category)</option>
            {categories.map((cat) => (
              <option key={cat.categoryID} value={cat.categoryID}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="admin-add-category-form-group">
          <label>Upload Category Image:</label>
          <div className="admin-add-category-file-input">
            <label htmlFor="categoryImageUpload">
              {imagePreview ? (
                <div className="admin-add-category-image-preview">
                  <img 
                    src={imagePreview} 
                    alt="Category Preview" 
                    style={{ 
                      maxWidth: '300px', 
                      maxHeight: '300px', 
                      objectFit: 'contain',
                      borderRadius: '4px'
                    }} 
                  />
                  <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                    Click to change image
                  </div>
                </div>
              ) : (
                <div className="admin-add-category-upload-placeholder" style={{
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  height: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backgroundColor: '#f8f9fa'
                }}>
                  <i className="fas fa-cloud-upload-alt" style={{ fontSize: '40px', color: '#ccc', marginBottom: '10px' }}></i>
                  <span style={{ color: '#666', fontSize: '16px', marginBottom: '5px' }}>Click to select an image</span>
                  <span style={{ color: '#999', fontSize: '13px' }}>Recommended size: 800×600px, max 5MB</span>
                
                </div>
              )}
              <input
                id="categoryImageUpload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          {imagePreview && (
            <div style={{ marginTop: '15px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleClearImage}
                style={{ 
                  padding: '8px 15px', 
                  fontSize: '14px',
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  border: '1px solid #ffcdd2',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffcdd2';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffebee';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <i className="fas fa-times"></i> Remove Image
              </button>
            </div>
          )}
        </div>

        <div className="admin-add-category-buttons-container">
          <button
            type="submit"
            disabled={isLoading || !name.trim()}
            className="admin-add-category-btn-primary"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Adding...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> Add Category
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminAddCategory;